/**
 * `POST /api/v2/prospectingTools/lookup` (WEB-12811). The types mirror
 * base/src/core/services/prospectingAgent/lookup/types.ts, which the UI cannot import.
 */

export type LookupChannel = 'email' | 'phoneNumber';

export type LookupQueryKind =
	| 'linkedinUrl'
	| 'linkedinCompany'
	| 'email'
	| 'domain'
	| 'orgNumber'
	| 'nameAndCompany'
	| 'unknown';

export type LookupStatus =
	| 'found'
	| 'notFound'
	| 'unknownQuery'
	| 'noConnectors'
	| 'outOfCredits'
	| 'notAvailableEuOnly'
	| 'failed';

export type LookupProvider = 'apollo' | 'hunter' | 'lusha';

export type LookupValue = {
	value: string;
	/**
	 * `input` is the value the rep pasted; `airscale` is Airscale ahead of the waterfall, on a LinkedIn
	 * profile under AIRSCALE_INTEGRATION (WEB-13237); the rest are the waterfall's providers.
	 */
	provider: 'input' | 'airscale' | LookupProvider;
	step: number | null;
	verification: string | null;
};

export type LookupPerson = {
	name: string | null;
	title: string | null;
	linkedinUrl: string | null;
	companyName: string | null;
	email: LookupValue | null;
	phoneNumber: LookupValue | null;
	crm: { contactId: number; clientId: number | null; matchedBy: 'email' | 'name' } | null;
};

/** Which source found a company: the register, the customer's CRM, or Apollo (WEB-13098). */
export type LookupCompanySource = 'register' | 'crm' | 'airscale' | 'apollo';

export type LookupCompany = {
	source: LookupCompanySource;
	/** Null for a company found only in the CRM or at Apollo. */
	prospectingId: string | null;
	name: string;
	orgNumber: string | null;
	countryCode: string | null;
	domain: string | null;
	firmographics: {
		employees?: number;
		revenue?: number;
		foundedYear?: number;
		country?: string;
		city?: string;
	} | null;
	sectors: string[];
	crm: { clientId: number; name: string; matchedBy: 'orgNo' | 'domain' | 'name' } | null;
	decisionMakers: { name: string; role: string | null }[];
};

export type LookupResult = {
	query: string;
	kind: LookupQueryKind;
	status: LookupStatus;
	/** Providers on Upsales' keys were skipped: the org's AI credits are used up (WEB-12978). */
	skippedForCredits?: boolean;
	/** EU ONLY: the customer's own provider accounts outside the EU that this lookup asked (WEB-13007). */
	outsideEuOwnKeys?: LookupProvider[];
	/** With `noConnectors`: the providers that could have answered, any one of which would do (WEB-13097). */
	needs?: LookupProvider[];
	person: LookupPerson | null;
	company: LookupCompany | null;
	aiCredits: number;
	/** The user's stored lookup of this query, found then (ISO): nothing was paid this time (WEB-14049). */
	storedAt?: string;
};

/**
 * The API answers one input today, so this sends one and returns its result. Lookup is a GENERIC
 * route, which sends its result as-is - `{ results }`, no `{ error, data }` envelope - like the
 * waterfall and Airscale settings beside it (WEB-13771).
 */
export async function runLookup(
	query: string,
	find: LookupChannel[],
	/** Run it again instead of showing the user's stored lookup of it (WEB-14049). */
	refresh = false
): Promise<LookupResult> {
	const res = await fetch('/api/v2/prospectingTools/lookup', {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({ inputs: [{ query, find, ...(refresh ? { refresh } : {}) }] })
	});
	const json = await res.json();
	if (!res.ok || json?.error || !json?.results?.[0]) {
		throw new Error(json?.error?.msg || `Lookup failed (${res.status})`);
	}
	return json.results[0];
}

/** One of the user's lookups from the last 15 days (WEB-14049). */
export type RecentLookup = { query: string; kind: LookupQueryKind; label: string | null; foundAt: string };

/** The user's own lookups from the last 15 days, newest first. A GENERIC route: the body is bare. */
export async function fetchRecentLookups(): Promise<RecentLookup[]> {
	const res = await fetch('/api/v2/prospectingTools/lookup/recent');
	const json = await res.json();
	if (!res.ok || json?.error) throw new Error(json?.error?.msg || `Recent lookups failed (${res.status})`);
	return json.lookups ?? [];
}

/**
 * Phone on demand (WEB-13365): a person lookup finds the email, and the phone - by far the dearest
 * channel - only when the rep asks. `ask` offers it, `finding` while it runs, `asked` once it has.
 */
export type PhoneOnDemand = { state: 'ask' | 'finding' | 'asked'; onFind: () => void };

/**
 * One channel's run folded into the shown result, with both runs' credits. The runs can come in
 * either order (the extension offers both from the start, WEB-13771), so a first run that named no
 * one gives way to one that did.
 */
export const withChannel = (result: LookupResult, other: LookupResult, channel: LookupChannel): LookupResult => {
	const aiCredits = result.aiCredits + other.aiCredits;
	if (!result.person) return { ...other, aiCredits };
	return {
		...result,
		person: { ...result.person, [channel]: other.person?.[channel] ?? null },
		skippedForCredits: result.skippedForCredits || other.skippedForCredits,
		aiCredits
	};
};

/** Apollo says `verified`, Hunter says `valid`. */
export const isVerified = (value: LookupValue) =>
	['verified', 'valid'].includes((value.verification ?? '').toLowerCase());

export const PROVIDER_NAMES: Record<LookupProvider | 'airscale', string> = {
	airscale: 'Airscale',
	apollo: 'Apollo',
	hunter: 'Hunter',
	lusha: 'Lusha'
};

/** What each status other than `found` says, wherever a lookup answer is shown (ResultView, the extension's Add person tab). */
export const STATUS_KEYS: Record<Exclude<LookupStatus, 'found'>, string> = {
	notFound: 'prospectingTools.lookup.status.notFound',
	unknownQuery: 'prospectingTools.lookup.status.unknownQuery',
	noConnectors: 'prospectingTools.lookup.status.noConnectors',
	outOfCredits: 'prospectingTools.lookup.status.outOfCredits',
	notAvailableEuOnly: 'prospectingTools.lookup.status.notAvailableEuOnly',
	failed: 'prospectingTools.lookup.status.failed'
};

/** One person at a company, from "People at this company" (WEB-13377): a profile, no email or phone yet. */
export type CompanyPerson = { name: string; title: string | null; linkedinUrl: string };

export type CompanyPeopleResult = {
	status: 'found' | 'notFound' | 'notAvailable' | 'outOfCredits' | 'failed';
	people: CompanyPerson[];
	total: number;
	nextCursor: string | null;
	aiCredits: number;
};

/** `POST /api/v2/prospectingTools/lookup/people`: who works at a company, by role, a page at a time. Sent as-is, like Lookup. */
export async function findCompanyPeople(request: {
	/** The company by its website, or by its LinkedIn page - exact, where the rep is on it (WEB-13771). */
	domain?: string;
	linkedinCompanyUrl?: string;
	role?: string;
	cursor?: string;
}): Promise<CompanyPeopleResult> {
	const res = await fetch('/api/v2/prospectingTools/lookup/people', {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify(request)
	});
	const json = await res.json();
	if (!res.ok || json?.error || !json?.status) {
		throw new Error(json?.error?.msg || `People search failed (${res.status})`);
	}
	return json;
}
